import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Clock, LogIn, LogOut, Coffee, ChevronDown, Timer, Play
} from 'lucide-react';
import timeTrackingService from '../../services/time-tracking.service';
import { TimeEntry } from '../../types/time-tracking.types';
import { LiveTimer } from '../time-tracking/LiveTimer';

interface TimeTrackingWidgetProps {
  isOpen?: boolean;
  onToggle?: () => void;
}

const TimeTrackingWidget: React.FC<TimeTrackingWidgetProps> = ({ isOpen, onToggle }) => {
  const [todayEntry, setTodayEntry] = useState<TimeEntry | null>(null);
  const [loading, setLoading] = useState(true);
  const [internalIsExpanded, setInternalIsExpanded] = useState(false);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [isOnBreak, setIsOnBreak] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLDivElement>(null);

  // Use external control if provided, otherwise use internal state
  const isExpanded = isOpen !== undefined ? isOpen : internalIsExpanded;
  const _setIsExpanded = onToggle || ((open: boolean) => setInternalIsExpanded(open));

  // Update clock every second
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  // Load today's entry on mount
  useEffect(() => {
    loadTodayEntry();
    // Reload every 30 seconds
    const interval = setInterval(loadTodayEntry, 30000);
    return () => clearInterval(interval);
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        buttonRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        if (onToggle && isExpanded) {
          onToggle();
        } else {
          setInternalIsExpanded(false);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isExpanded, onToggle]);

  const loadTodayEntry = async () => {
    try {
      setLoading(true);
      const result = await timeTrackingService.getTodayEntry();
      if (result.success && result.data) {
        setTodayEntry(result.data);
        setIsOnBreak(result.data.status === 'on_break');
      } else {
        // No entry today yet
        setTodayEntry(null);
        setIsOnBreak(false);
      }
    } catch (error) {
      console.error('Fehler beim Laden der heutigen Zeiterfassung:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCheckIn = async () => {
    try {
      console.log('Check-in wird ausgeführt...');
      const result = await timeTrackingService.checkIn('Office');
      console.log('Check-in Ergebnis:', result);
      if (result.success) {
        await loadTodayEntry();
        alert('Check-in erfolgreich!');
      } else {
        alert(`Check-in fehlgeschlagen: ${result.error || 'Unbekannter Fehler'}`);
      }
    } catch (error: any) {
      console.error('Check-in Fehler:', error);
      alert(`Check-in fehlgeschlagen: ${error.message || 'Netzwerkfehler'}`);
    }
  };

  const handleCheckOut = async () => {
    try {
      const result = await timeTrackingService.checkOut();
      if (result.success) {
        await loadTodayEntry();
        alert('Check-out erfolgreich!');
      } else {
        alert(`Check-out fehlgeschlagen: ${result.error}`);
      }
    } catch (error: any) {
      console.error('Check-out Fehler:', error);
      alert(`Check-out fehlgeschlagen: ${error.message}`);
    }
  };

  const handleStartBreak = async () => {
    try {
      if (!todayEntry) return;
      const result = await timeTrackingService.startBreak(todayEntry.id, 'other');
      if (result.success) {
        await loadTodayEntry();
        alert('Pause gestartet!');
      } else {
        alert(`Pause starten fehlgeschlagen: ${result.error}`);
      }
    } catch (error: any) {
      console.error('Pause starten Fehler:', error);
      alert(`Pause starten fehlgeschlagen: ${error.message}`);
    }
  };

  const handleEndBreak = async () => {
    try {
      if (!todayEntry?.breaks?.length) return;
      const activeBreak = todayEntry.breaks.find(b => !b.break_end);
      if (!activeBreak) return;

      const result = await timeTrackingService.endBreak(activeBreak.id);
      if (result.success) {
        await loadTodayEntry();
        alert('Pause beendet!');
      } else {
        alert(`Pause beenden fehlgeschlagen: ${result.error}`);
      }
    } catch (error: any) {
      console.error('Pause beenden Fehler:', error);
      alert(`Pause beenden fehlgeschlagen: ${error.message}`);
    }
  };

  const isCheckedIn = todayEntry && todayEntry.status !== 'checked_out';
  const statusColor = todayEntry ? timeTrackingService.getStatusColor(todayEntry.status) : '#6B7280';

  if (loading) {
    return (
      <div style={{
        marginRight: 'var(--spacing-md)',
        padding: '0.5rem 1rem',
        background: 'var(--bg-primary)',
        border: '1px solid var(--border-color)',
        borderRadius: 'var(--radius-lg)',
        minWidth: '180px'
      }}>
        <p style={{ fontSize: '0.75rem', color: 'var(--text-tertiary)' }}>Lädt...</p>
      </div>
    );
  }

  return (
    <div
      ref={buttonRef}
      style={{
        position: 'relative',
        marginRight: 'var(--spacing-md)'
      }}
    >
      {/* Main Widget Button */}
      <motion.button
        onClick={() => {
          if (onToggle) {
            onToggle();
          } else {
            setInternalIsExpanded(!internalIsExpanded);
          }
        }}
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--spacing-sm)',
          padding: '0.5rem 1rem',
          background: isCheckedIn
            ? `linear-gradient(135deg, ${statusColor}33, ${statusColor}1a)`
            : 'var(--bg-primary)',
          border: `1px solid ${isCheckedIn ? `${statusColor}4d` : 'var(--border-color)'}`,
          borderRadius: 'var(--radius-lg)',
          cursor: 'pointer',
          position: 'relative',
          minWidth: '180px',
          boxShadow: 'var(--shadow-sm)'
        }}
      >
        {/* Tracking Status Indicator */}
        {isCheckedIn && (
          <motion.div
            animate={{ opacity: [0.5, 1, 0.5] }}
            transition={{ duration: 2, repeat: Infinity }}
            style={{
              position: 'absolute',
              top: '-4px',
              right: '-4px',
              width: '12px',
              height: '12px',
              background: statusColor,
              borderRadius: '50%',
              border: '2px solid var(--bg-primary)'
            }}
          />
        )}

        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--spacing-xs)'
        }}>
          {isCheckedIn ? (
            <Timer style={{ width: 18, height: 18, color: statusColor }} />
          ) : (
            <Clock style={{ width: 18, height: 18, color: 'var(--text-secondary)' }} />
          )}
          <div style={{ textAlign: 'left' }}>
            <p style={{
              fontSize: '0.75rem',
              color: 'var(--text-tertiary)',
              marginBottom: '0.125rem'
            }}>
              {isCheckedIn
                ? (isOnBreak ? 'Pause' : 'Arbeitet')
                : 'Zeit erfassen'}
            </p>
            <p style={{
              fontSize: '0.875rem',
              fontWeight: 600,
              color: isCheckedIn ? statusColor : 'var(--text-primary)',
              fontFeatureSettings: '"tnum"'
            }}>
              {isCheckedIn && todayEntry?.check_in
                ? timeTrackingService.formatTime(todayEntry.check_in)
                : currentTime.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })}
            </p>
          </div>
        </div>

        <ChevronDown
          style={{
            width: 16,
            height: 16,
            color: 'var(--text-tertiary)',
            transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 0.2s'
          }}
        />
      </motion.button>

      {/* Expanded Panel */}
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            ref={dropdownRef}
            initial={{ opacity: 0, y: -10, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -10, scale: 0.95 }}
            transition={{ duration: 0.2 }}
            style={{
              position: 'absolute',
              top: 'calc(100% + var(--spacing-sm))',
              right: 0,
              width: '420px',
              background: 'var(--bg-primary)',
              border: '1px solid var(--border-color)',
              borderRadius: 'var(--radius-lg)',
              padding: 'var(--spacing-lg)',
              boxShadow: '0 20px 40px rgba(0, 0, 0, 0.4), 0 0 0 1px rgba(0, 0, 0, 0.1)',
              zIndex: 1000
            }}
          >
            {/* Live Timer (nur wenn eingecheckt) */}
            {isCheckedIn && todayEntry && (
              <div style={{ marginBottom: 'var(--spacing-md)' }}>
                <LiveTimer
                  checkInTime={todayEntry.check_in!}
                  totalBreakTime={todayEntry.total_break_time || '00:00:00'}
                />
              </div>
            )}

            {/* Quick Stats */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(3, 1fr)',
              gap: 'var(--spacing-sm)',
              marginBottom: 'var(--spacing-md)'
            }}>
              <div style={{
                padding: 'var(--spacing-sm)',
                background: 'var(--bg-secondary)',
                borderRadius: 'var(--radius-md)',
                textAlign: 'center',
                border: '1px solid var(--border-color)'
              }}>
                <p style={{ fontSize: '0.688rem', color: 'var(--text-tertiary)', marginBottom: '0.25rem' }}>
                  Check-in
                </p>
                <p style={{ fontSize: '0.875rem', fontWeight: 600, color: 'var(--text-primary)' }}>
                  {todayEntry?.check_in ? timeTrackingService.formatTime(todayEntry.check_in) : '-'}
                </p>
              </div>
              <div style={{
                padding: 'var(--spacing-sm)',
                background: 'var(--bg-secondary)',
                borderRadius: 'var(--radius-md)',
                textAlign: 'center',
                border: '1px solid var(--border-color)'
              }}>
                <p style={{ fontSize: '0.688rem', color: 'var(--text-tertiary)', marginBottom: '0.25rem' }}>
                  Check-out
                </p>
                <p style={{ fontSize: '0.875rem', fontWeight: 600, color: 'var(--text-primary)' }}>
                  {todayEntry?.check_out ? timeTrackingService.formatTime(todayEntry.check_out) : '-'}
                </p>
              </div>
              <div style={{
                padding: 'var(--spacing-sm)',
                background: 'var(--bg-secondary)',
                borderRadius: 'var(--radius-md)',
                textAlign: 'center',
                border: '1px solid var(--border-color)'
              }}>
                <p style={{ fontSize: '0.688rem', color: 'var(--text-tertiary)', marginBottom: '0.25rem' }}>
                  Pausen
                </p>
                <p style={{ fontSize: '0.875rem', fontWeight: 600, color: 'var(--text-primary)' }}>
                  {todayEntry?.breaks?.length || 0}x
                </p>
              </div>
            </div>

            {/* Control Buttons - IMMER ALLE BUTTONS ANZEIGEN */}
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 'var(--spacing-sm)',
              marginBottom: 'var(--spacing-md)'
            }}>
              {/* Check-in / Check-out Button */}
              {!isCheckedIn ? (
                <button
                  onClick={handleCheckIn}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 'var(--spacing-xs)',
                    padding: '0.875rem',
                    background: 'linear-gradient(135deg, #10B981, #059669)',
                    border: 'none',
                    borderRadius: 'var(--radius-md)',
                    color: 'white',
                    fontSize: '0.875rem',
                    fontWeight: 600,
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    boxShadow: '0 2px 8px rgba(16, 185, 129, 0.3)'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(16, 185, 129, 0.4)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 2px 8px rgba(16, 185, 129, 0.3)';
                  }}
                >
                  <LogIn style={{ width: 18, height: 18 }} />
                  Check-in
                </button>
              ) : (
                <button
                  onClick={handleCheckOut}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 'var(--spacing-xs)',
                    padding: '0.875rem',
                    background: 'linear-gradient(135deg, #3B82F6, #2563EB)',
                    border: 'none',
                    borderRadius: 'var(--radius-md)',
                    color: 'white',
                    fontSize: '0.875rem',
                    fontWeight: 600,
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    boxShadow: '0 2px 8px rgba(59, 130, 246, 0.3)'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(59, 130, 246, 0.4)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 2px 8px rgba(59, 130, 246, 0.3)';
                  }}
                >
                  <LogOut style={{ width: 18, height: 18 }} />
                  Check-out
                </button>
              )}

              {/* Pause Buttons - NUR WENN EINGECHECKT */}
              {isCheckedIn && (
                !isOnBreak ? (
                  <button
                    onClick={handleStartBreak}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 'var(--spacing-xs)',
                      padding: '0.875rem',
                      background: 'linear-gradient(135deg, #F59E0B, #D97706)',
                      border: 'none',
                      borderRadius: 'var(--radius-md)',
                      color: 'white',
                      fontSize: '0.875rem',
                      fontWeight: 600,
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      boxShadow: '0 2px 8px rgba(245, 158, 11, 0.3)'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-2px)';
                      e.currentTarget.style.boxShadow = '0 4px 12px rgba(245, 158, 11, 0.4)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = '0 2px 8px rgba(245, 158, 11, 0.3)';
                    }}
                  >
                    <Coffee style={{ width: 18, height: 18 }} />
                    Pause starten
                  </button>
                ) : (
                  <button
                    onClick={handleEndBreak}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 'var(--spacing-xs)',
                      padding: '0.875rem',
                      background: 'linear-gradient(135deg, #10B981, #059669)',
                      border: 'none',
                      borderRadius: 'var(--radius-md)',
                      color: 'white',
                      fontSize: '0.875rem',
                      fontWeight: 600,
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      boxShadow: '0 2px 8px rgba(16, 185, 129, 0.3)'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-2px)';
                      e.currentTarget.style.boxShadow = '0 4px 12px rgba(16, 185, 129, 0.4)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = '0 2px 8px rgba(16, 185, 129, 0.3)';
                    }}
                  >
                    <Play style={{ width: 18, height: 18 }} />
                    Pause beenden
                  </button>
                )
              )}
            </div>

            {/* Today's Timeline */}
            {todayEntry && todayEntry.breaks && todayEntry.breaks.length > 0 && (
              <div>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: 'var(--spacing-sm)'
                }}>
                  <h4 style={{
                    fontSize: '0.875rem',
                    fontWeight: 600,
                    color: 'var(--text-primary)'
                  }}>
                    Heutige Pausen
                  </h4>
                </div>

                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 'var(--spacing-xs)'
                }}>
                  {todayEntry.breaks.map((breakItem, index) => (
                    <div
                      key={breakItem.id}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 'var(--spacing-sm)',
                        padding: 'var(--spacing-sm)',
                        background: 'var(--bg-secondary)',
                        borderRadius: 'var(--radius-sm)',
                        border: '1px solid var(--border-color)'
                      }}
                    >
                      {/* Type Icon */}
                      <div style={{
                        width: '24px',
                        height: '24px',
                        borderRadius: '50%',
                        background: '#F59E0B33',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0
                      }}>
                        <Coffee style={{ width: 12, height: 12, color: '#F59E0B' }} />
                      </div>

                      {/* Entry Details */}
                      <div style={{ flex: 1 }}>
                        <div style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'start'
                        }}>
                          <div>
                            <p style={{
                              fontSize: '0.813rem',
                              fontWeight: 500,
                              color: 'var(--text-primary)'
                            }}>
                              Pause {index + 1}
                            </p>
                            <p style={{
                              fontSize: '0.688rem',
                              color: 'var(--text-tertiary)'
                            }}>
                              {timeTrackingService.formatTime(breakItem.break_start)} - {breakItem.break_end ? timeTrackingService.formatTime(breakItem.break_end) : 'läuft...'}
                            </p>
                          </div>
                          <span style={{
                            fontSize: '0.75rem',
                            fontWeight: 600,
                            color: '#F59E0B'
                          }}>
                            {breakItem.duration ? timeTrackingService.formatDuration(breakItem.duration) : '-'}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Arbeitszeit heute */}
            {todayEntry && todayEntry.total_work_time && (
              <div style={{
                marginTop: 'var(--spacing-md)',
                paddingTop: 'var(--spacing-md)',
                borderTop: '1px solid var(--border-color)',
                textAlign: 'center'
              }}>
                <p style={{
                  fontSize: '0.813rem',
                  color: 'var(--text-secondary)',
                  marginBottom: '0.25rem'
                }}>
                  Gesamte Arbeitszeit heute
                </p>
                <p style={{
                  fontSize: '1.5rem',
                  fontWeight: 700,
                  color: 'var(--accent-primary)'
                }}>
                  {timeTrackingService.formatDuration(todayEntry.total_work_time)}
                </p>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default TimeTrackingWidget;
